import React, { useState, useEffect } from "react";
import "./ContactPage.css";
import { useResponsiveQueries } from "../../utils/constants";
import { motion } from "framer-motion";
import { fadeIn } from "../../variants";
import {
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import Flag from "react-world-flags";
import contactImage from "../../assets/images/contact-us.png";
import emailjs from "@emailjs/browser";
import { countryCodes, countryCodeMap } from "../../utils/countryCodes";

const ContactRow1 = () => {
  const { MOBILE_WIDTH } = useResponsiveQueries();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    telegram: "",
    whatsapp: "",
    message: "",
  });
  const [countryCode, setCountryCode] = useState("+1");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [errors, setErrors] = useState({
    telegram: "",
    whatsapp: "",
  });
  const [loadedImages, setLoadedImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Detect user's country and set default country code
  useEffect(() => {
    fetch("https://get.geojs.io/v1/ip/geo.json")
      .then((res) => res.json())
      .then((data) => {
        const userCountryCode = data.country_code;
        const dialCode = countryCodeMap[userCountryCode];
        if (dialCode) {
          setCountryCode(dialCode);
        }
      })
      .catch((err) => console.log("Could not detect location:", err));
  }, []);

  const handleImageLoad = (imageName) => {
    setLoadedImages((prev) => ({ ...prev, [imageName]: true }));
  };

  // Validate Telegram input
  const validateTelegram = (value) => {
    if (!value) return ""; // Optional field

    // Check if it's a URL
    if (value.includes("t.me/") || value.includes("telegram.me/")) {
      const urlPattern = /^https?:\/\/(t\.me|telegram\.me)\/(@?[\w]{5,32})$/;
      if (!urlPattern.test(value.trim())) {
        return "Invalid Telegram URL format";
      }
    } else {
      // Check if it's a username
      const usernamePattern = /^@?[\w]{5,32}$/;
      if (!usernamePattern.test(value.trim())) {
        return "Username must be 5-32 characters (letters, numbers, underscores)";
      }
      // Must start with @ if not a URL
      if (!value.trim().startsWith("@") && !value.includes("/")) {
        return "Username must start with @";
      }
    }
    return "";
  };

  // Validate WhatsApp number
  const validateWhatsApp = (number) => {
    if (!number) return ""; // Optional field

    const cleanNumber = number.replace(/[^\d]/g, "");
    if (cleanNumber.length < 7 || cleanNumber.length > 15) {
      return "Phone number must be 7-15 digits";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate on change
    if (name === "telegram") {
      setErrors({ ...errors, telegram: validateTelegram(value) });
    }
  };

  const handleWhatsAppChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, ""); // Only digits
    setWhatsappNumber(value);

    const fullNumber = countryCode + value;
    setFormData({ ...formData, whatsapp: fullNumber });
    setErrors({ ...errors, whatsapp: validateWhatsApp(value) });
  };

  const handleCountryCodeChange = (e) => {
    const newCode = e.target.value;
    setCountryCode(newCode);

    const fullNumber = newCode + whatsappNumber;
    setFormData({ ...formData, whatsapp: fullNumber });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate optional fields if filled
    const telegramError = validateTelegram(formData.telegram);
    const whatsappError = validateWhatsApp(whatsappNumber);

    if (telegramError || whatsappError) {
      setErrors({
        telegram: telegramError,
        whatsapp: whatsappError,
      });
      setNotification({
        open: true,
        message: "Please fix the validation errors before submitting",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      // Get credentials from environment variables
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      // Validate environment variables
      if (!serviceId || !templateId || !publicKey) {
        throw new Error(
          "EmailJS configuration is missing. Please check your .env file."
        );
      }

      // Process Telegram input - extract username from URL if provided
      const processTelegram = (input) => {
        if (!input) return "";

        // Remove whitespace
        const cleaned = input.trim();

        // Check if it's a Telegram URL
        if (cleaned.includes("t.me/") || cleaned.includes("telegram.me/")) {
          // Extract username from URL
          const match = cleaned.match(/(?:t\.me|telegram\.me)\/(@?[\w]+)/);
          if (match && match[1]) {
            // Remove @ if present, we'll add it back
            return match[1].replace(/^@/, "");
          }
        }

        // If it's just a username, remove @ if present
        return cleaned.replace(/^@/, "");
      };

      // Process WhatsApp - remove any non-numeric characters except +
      const processWhatsApp = (input) => {
        if (!input) return "";
        return input.trim().replace(/[^\d+]/g, "");
      };

      const telegramUsername = processTelegram(formData.telegram);
      const whatsappNumber = processWhatsApp(formData.whatsapp);

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        telegram: telegramUsername,
        whatsapp: whatsappNumber,
        message: formData.message,
        to_name: "101 Best White Label Team",
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      setNotification({
        open: true,
        message: "Message sent successfully! We'll get back to you soon.",
        severity: "success",
      });

      // Clear form
      setFormData({
        name: "",
        email: "",
        telegram: "",
        whatsapp: "",
        message: "",
      });
    } catch (error) {
      console.error("EmailJS Error:", error);
      setNotification({
        open: true,
        message:
          "Failed to send message. Please try again or contact us directly.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const isFormValid = formData.name && formData.email && formData.message;

  return (
    <div className="contact-row1-container">
      <div className="contact-content">
        {!MOBILE_WIDTH && (
          <motion.div
            variants={fadeIn("right", 0.2)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: false, amount: 0 }}
            className="contact-image-section"
          >
            <img
              className={`contact-girl-image ${
                loadedImages.contactGirl ? "loaded" : ""
              }`}
              src={contactImage}
              alt="Contact Us"
              loading="lazy"
              onLoad={() => handleImageLoad("contactGirl")}
            />
          </motion.div>
        )}

        <motion.div
          variants={fadeIn("left", 0.2)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0 }}
          className="contact-form-section"
        >
          <div className="contact-form-header">
            <div
              className="row-title"
              style={{ fontSize: "clamp(40px, 5.5vw, 96px)", color: "#0D65D9" }}
            >
              CONTACT US
            </div>
            <div
              className="row-subtitle"
              style={{
                fontSize: "clamp(16px, 2vw, 24px)",
                marginBottom: "32px",
              }}
            >
              We'd Love to Hear From You
            </div>
          </div>

          <form onSubmit={handleSubmit} className="contact-form">
            <TextField
              id="contact-input"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "12px",
                },
              }}
            />
            <TextField
              id="contact-input"
              name="email"
              type="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "12px",
                },
              }}
            />
            <TextField
              id="contact-input"
              name="telegram"
              placeholder="@username or https://t.me/username"
              value={formData.telegram}
              onChange={handleChange}
              error={!!errors.telegram}
              helperText={
                errors.telegram || "Example: @johndoe or https://t.me/johndoe"
              }
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "12px",
                },
                "& .MuiFormHelperText-root": {
                  fontFamily: "Proxima Nova",
                  fontSize: "12px",
                  marginTop: "6px",
                  color: "#FFFFFF !important",
                },
                "& .MuiFormHelperText-root.Mui-error": {
                  color: "#ff6b6b !important",
                },
              }}
            />

            {/* WhatsApp with Country Code Selector */}
            <div style={{ display: "flex", gap: "12px", width: "100%" }}>
              <FormControl
                sx={{
                  minWidth: "140px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    background: "rgba(255, 255, 255, 0.95)",
                  },
                }}
              >
                <Select
                  value={countryCode}
                  onChange={handleCountryCodeChange}
                  displayEmpty
                  renderValue={(selected) => {
                    const country = countryCodes.find(
                      (c) => c.code === selected
                    );
                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "start",
                          gap: "10px",
                        }}
                      >
                        <Flag
                          code={country?.country || "US"}
                          style={{
                            width: "28px",
                            height: "20px",
                            borderRadius: "4px",
                            objectFit: "cover",
                          }}
                        />
                        <span style={{ fontWeight: "600" }}>
                          {country?.code || selected}
                        </span>
                      </div>
                    );
                  }}
                  sx={{
                    height: "56px",
                    fontFamily: "Proxima Nova",
                    fontSize: "17px",
                    "& .MuiSelect-select": {
                      padding: "18px 20px",
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                >
                  {countryCodes.map((country) => (
                    <MenuItem
                      key={country.code}
                      value={country.code}
                      sx={{
                        fontFamily: "Proxima Nova",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "10px 16px",
                      }}
                    >
                      <Flag
                        code={country.country}
                        style={{
                          width: "32px",
                          height: "24px",
                          borderRadius: "4px",
                          objectFit: "cover",
                        }}
                      />
                      <span style={{ fontWeight: "600" }}>{country.code}</span>
                      <span
                        style={{
                          color: "#666",
                          fontSize: "14px",
                          marginLeft: "auto",
                        }}
                      >
                        {country.name}
                      </span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                id="contact-input"
                placeholder="Phone Number"
                value={whatsappNumber}
                onChange={handleWhatsAppChange}
                error={!!errors.whatsapp}
                helperText={errors.whatsapp || "Enter your WhatsApp number"}
                fullWidth
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    borderRadius: "12px",
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: "Proxima Nova",
                    fontSize: "12px",
                    marginTop: "6px",
                    color: "#FFFFFF !important",
                  },
                  "& .MuiFormHelperText-root.Mui-error": {
                    color: "#ff6b6b !important",
                  },
                }}
              />
            </div>
            <TextField
              id="contact-input"
              name="message"
              placeholder="Your Message"
              value={formData.message}
              onChange={handleChange}
              required
              fullWidth
              multiline
              rows={6}
              sx={{
                "& .MuiInputBase-root": {
                  padding: "0px",
                  borderRadius: "12px",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "16px 14px",
                },
              }}
            />
            <Button
              type="submit"
              id="action-button"
              disabled={!isFormValid || loading}
              sx={{ maxWidth: "100%" }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
              ) : (
                "Send Message"
              )}
            </Button>
          </form>
        </motion.div>
      </div>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={motion.div}
        TransitionProps={{
          initial: { opacity: 0, y: 50, scale: 0.9 },
          animate: { opacity: 1, y: 0, scale: 1 },
          exit: { opacity: 0, y: 50, scale: 0.9 },
          transition: { duration: 0.3, ease: "easeOut" },
        }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{
            width: "100%",
            minWidth: "300px",
            fontSize: "16px",
            fontFamily: "Proxima Nova",
            fontWeight: 600,
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.3)",
            borderRadius: "12px",
            "& .MuiAlert-icon": {
              fontSize: "28px",
            },
            ...(notification.severity === "success" && {
              background: "linear-gradient(135deg, #0D65D9 0%, #0051BC 100%)",
            }),
            ...(notification.severity === "error" && {
              background: "linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)",
            }),
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ContactRow1;
